import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import ShineBorder from '@cobliteam/ui-business/components/border/ShineBorder';
import type { ReactNode } from 'react';

/** Tema visual do card por tipo: classes da moldura + paleta da borda animada + ícone/título. */
export interface NotificationCardTheme {
  containerClassName: string;
  shineColors: string[];
  shineBorderWidth?: number;
  shineDuration?: number;
  iconWrapClassName: string;
  titleClassName: string;
}

interface NotificationCardShellProps {
  theme: NotificationCardTheme;
  iconName: IconNames;
  /** Badge de urgência/tipo no header (slot — o tipo monta seu próprio Tag). */
  badge: ReactNode;
  /** Título já renderizado (ex.: com markup `<bold>` resolvido). */
  title: ReactNode;
  /** Descrição já renderizada (nó completo, com suas próprias classes). */
  description: ReactNode;
  /** Linha de local opcional (nó de texto). */
  location?: ReactNode;
  dismissLabel: string;
  onDismiss: () => void;
  /** CTA principal (ex.: AIButton do risco) com o onAction já ligado pelo tipo. */
  action: ReactNode;
  className?: string;
}

/**
 * Caixa visual compartilhada do card de notificação (ícone + badge + fechar, título, descrição, local,
 * ações). Reusada entre o card flutuante (qualquer surface de card) e o toast (sonner) para terem o MESMO
 * layout. Cores/ícone/cópia vêm do tipo via `theme` + slots, então cada tipo customiza mantendo a estrutura.
 */
const NotificationCardShell = ({ theme, iconName, badge, title, description, location, dismissLabel, onDismiss, action, className }: NotificationCardShellProps) => (
  <div className={cn('relative flex w-full flex-col gap-[9px] rounded-2xl border-solid p-[14px]', theme.containerClassName, className)}>
    <ShineBorder shineColor={theme.shineColors} borderWidth={theme.shineBorderWidth ?? 2} duration={theme.shineDuration ?? 6} />

    <div className='flex w-full items-center justify-between'>
      <div className='flex items-center gap-2'>
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', theme.iconWrapClassName)} aria-hidden>
          <Icon name={iconName} size='sm' />
        </span>
        {badge}
      </div>
      <button
        type='button'
        onClick={onDismiss}
        aria-label={dismissLabel}
        className='flex size-6 cursor-pointer items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-neutral-100'
      >
        <Icon name='x' size='sm' />
      </button>
    </div>

    <Typography variant='description-title' className={cn('break-words leading-[1.35] [overflow-wrap:anywhere]', theme.titleClassName)}>
      {title}
    </Typography>

    {description}

    {location ? (
      <div className='flex items-start gap-1.5 text-gray-8'>
        <Icon name='map-pin' size='xs' className='mt-0.5 shrink-0' aria-hidden />
        <Typography variant='caption-xs' className='break-words leading-[1.4] [overflow-wrap:anywhere]'>
          {location}
        </Typography>
      </div>
    ) : null}

    <div className='flex items-center justify-between gap-3 pt-1'>
      <Button variant='ghost' color='default' size='sm' onClick={onDismiss}>
        {dismissLabel}
      </Button>
      {action}
    </div>
  </div>
);

export default NotificationCardShell;
